import { memo, type JSX } from 'react';
import { Text } from '@mantine/core';
import { IconMessageCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { TaskMiniMap } from '@/components/map/TaskMiniMap';
import { TaskComments } from '@/components/tasks/panel/TaskComments/TaskComments';
import {
  FONT_WEIGHT_SEMIBOLD,
  ICON_SIZE_MD,
  ICON_STROKE_WIDTH_DEFAULT,
} from '@/constants/ui';
import type { Task } from '@/types';

import styles from './FullscreenRightPanel.module.css';

interface FullscreenRightPanelProps {
  task: Task;
  /** Read-only comments (public share) — suppresses input + SignalR. */
  readOnly?: boolean;
  /** Hide the comments section entirely; the map then fills the panel. */
  showComments?: boolean;
}

export const FullscreenRightPanel = memo(function FullscreenRightPanel({
  task,
  readOnly = false,
  showComments = true,
}: FullscreenRightPanelProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <aside className={styles.rightPanel} role="complementary">
      <div
        className={clsx(
          styles.mapSection,
          !showComments && styles.mapSectionFull
        )}
      >
        <div
          className={clsx(
            styles.mapContainer,
            !showComments && styles.mapContainerFull
          )}
        >
          <TaskMiniMap task={task} />
        </div>
      </div>

      {showComments && (
        <div className={styles.commentsSection}>
          <div
            className={clsx(styles.sectionHeader, styles.commentsSectionHeader)}
          >
            <IconMessageCircle
              size={ICON_SIZE_MD}
              stroke={ICON_STROKE_WIDTH_DEFAULT}
            />
            <Text size="sm" fw={FONT_WEIGHT_SEMIBOLD}>
              {t('taskDetails.tabs.comments')}
            </Text>
          </div>
          <div className={styles.commentsContainer}>
            <TaskComments task={task} readOnly={readOnly} />
          </div>
        </div>
      )}
    </aside>
  );
});
